import { getDateFormat } from './getDateFormat';

const isISODate = (value: any) => {
  if (typeof value !== 'string' || isNaN(Date.parse(value))) {
    return false;
  }

  return value.length === 20 && !/\s/.test(value) && `${new Date(value).toISOString().split('.')[0]}Z` === value;
};

export const convertValues = <T>(
  object: Record<string, any>,
  enums?: Record<string, string[]>,
  cast?: Partial<Record<keyof T, any>>
) => {
  const newObject: any = {};

  const getValue = (k: any) => {
    const value = object[k];

    if (cast && typeof cast[k as keyof T] !== 'undefined' && Object.keys(cast).includes(k)) {
      return cast[k as keyof T];
    } else if (enums && Object.keys(enums).includes(k) && typeof enums[k][value] !== 'undefined') {
      return enums[k][value];
    } else if (typeof value === 'undefined' || value === '') {
      return undefined;
    } else if (isISODate(value)) {
      return new Date(value);
    } else if (typeof value === 'boolean') {
      return !!value;
    } else if (value === 'true' || value === 'false') {
      return value === 'true';
    } else if (!isNaN(value) && typeof value === 'string' && !value.includes('+')) {
      return parseInt(value, 10);
    } else {
      return value;
    }
  };

  for (const k in object) {
    if (object.hasOwnProperty(k)) {
      const value = object[k];

      if (typeof value === 'object' && !(value instanceof Date) && value !== null && !Array.isArray(value)) {
        newObject[k] = convertValues(
          value,
          enums,
          cast ? (typeof cast[k as keyof T] !== 'undefined' ? cast[k as keyof T] : undefined) : undefined
        );
      } else {
        newObject[k] = getValue(k);
      }
    }
  }

  return newObject as T;
};

export const convertValuesToString = (object: Record<string, any>) => {
  const newObject = object;

  Object.keys(object).forEach((key: string) => {
    newObject[key] =
      object[key] && typeof object[key] === 'object'
        ? convertValuesToString(object[key])
        : typeof object[key] === 'number' || object[key] === null
        ? String(object[key])
        : object[key];
  });

  return newObject;
};

const customPadStart = (value: number, digits: number) => {
  const val = Math.abs(value);
  const zeros = Math.max(0, digits - Math.floor(val).toString().length);

  let zeroString = Math.pow(10, zeros).toString().substr(1);

  if (value < 0) {
    zeroString = `-${zeroString}`;
  }

  return zeroString + val;
};

const getHoursMinutesOffset = () => {
  const offset = new Date().getTimezoneOffset() * -1;

  const hours = Math.floor(offset / 60);
  const minutes = Math.round((offset / 60 - hours) * 60);

  return `${hours >= 0 ? '+' : ''}${customPadStart(hours, 2)}:${customPadStart(minutes, 2)}`;
};

export const convertDateToRange = (key: string, dateString?: string, time?: number, timeAM?: boolean) => {
  if (!dateString) {
    return {};
  }

  if (typeof time === 'undefined') {
    const nextDayObj = new Date(`${dateString}T00:00:00Z`);

    nextDayObj.setTime(nextDayObj.getTime() + nextDayObj.getTimezoneOffset() * 60 * 1000);
    nextDayObj.setDate(nextDayObj.getDate() + 1);

    return {
      [`${key}Gte`]: `${dateString}T00:00:00${getHoursMinutesOffset()}`,
      [`${key}Lt`]: `${getDateFormat(nextDayObj)}T00:00:00${getHoursMinutesOffset()}`,
    };
  }

  const time24 = (!timeAM ? time + 1200 : time).toString();

  return {
    [key]: `${dateString}T${time24.substring(0, 2)}:${time24.slice(-2)}:00${getHoursMinutesOffset()}`,
  };
};

export const convertFileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      resolve(reader.result as string);
    };

    reader.onerror = (error) => reject(error);

    reader.readAsDataURL(file);
  });
};

export const base64ToFile = (base64: string, filename: string, mimeType: string): File => {
  const byteString = atob(base64.split(',')[1]);

  const ab = new ArrayBuffer(byteString.length);
  const ia = new Uint8Array(ab);
  for (let i = 0; i < byteString.length; i++) {
    ia[i] = byteString.charCodeAt(i);
  }

  const blob = new Blob([ab], { type: mimeType });

  return new File([blob], filename, { type: mimeType });
};

export const convertCleanBase64 = (base64String: string): string => {
  // Example string to strip
  // data:application/octet-stream;base64,AAAAJGZ0eXBo for HEIC format
  // data:image/jpg;base64,AAAAJGZ0eXBo for JPG format
  return base64String.replace(/^data:\w+\/.+;base64,/, '');
};
